import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Investigation } from '../../../../shared/models/admin/investigation.model';
import { Response } from '../../../../shared/models/api';
import { MasterData } from '../../../../shared/models/common';
import { MasterPriceData } from '../../../../shared/models/common/master-price-data.model';
import { Language, MasterData as masterDataList, LanguageMasterData } from '../../../../shared/models/master-data';
import { ManualAuditItemMaster } from '../../../../shared/models/master-data/manual-audit-item.model';
import { MedicationMaster } from '../../../../shared/models/master-data/medication.master.model';
import { ToDo  } from '../../../../shared/models/master-data/todo.master.model';
import { InventoryTimeConfig } from '../../../../shared/models/operations';
import { API_ENDPOINTS } from '../../../../shared/utils/api-endpoints';
import { ApiService } from '../api.service';

import { HANDLED_ERRORS } from 'src/app/shared/utils/http-context';

import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MasterDataService extends ApiService {
  private endpoint = API_ENDPOINTS.MASTER_DATA;

  createClinicalInvestigationData(data: Investigation): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'createInvestigationData',
      url: this.endpoint.clinicalInvestigationList(),
    });
  }

  updatePaymentStatus(data: any[]): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'updatePaymentStatus',
      url: this.endpoint.paymentStatusUpdate(), // Ensure you have this method in `endpoint`
    });
  }


  updateRrTypeMaster(data: any): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateRrTypeMaster',
      url: this.endpoint.rrTypeMasterUpdate(), // Ensure you have this method in `endpoint`
    });
  }
  
  

  createMasterData(
    data: Record<string, string | number>,
    masterType: string | unknown = null
  ): Observable<Response<MasterData>> {
    return this.post<Response<MasterData>>({
      body: data,
      metadata: { ...this.getHttpParams({ masterType: masterType }) },
      operation: 'createMasterData',
      url: this.endpoint.masterDataCreate(),
    });
  }

  createMasterDataByLanguage(data: Record<string, string>): Observable<Response<MasterData>> {
    return this.post<Response<MasterData>>({
      body: data,
      operation: 'createMasterDataByLanguage',
      url: this.endpoint.masterDataCreateByLanguage(),
    });
  }

  createMasterPriceData(data: MasterPriceData): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'createMasterPriceData',
      url: this.endpoint.createPrice(),
    });
  }

  deleteInvestigationData(investigationId: number): Observable<Response<Record<string, string>>> {
    return this.delete<Response<Record<string, string>>>({
      operation: 'deleteInvestigation',
      url: this.endpoint.investigationDataUpdate(investigationId),
    });
  }

  deleteMasterData(masterDataType: number, handledErrors: number[] = []): Observable<Response<Record<string, string>>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.delete<Response<Record<string, string>>>({
      operation: 'deleteMasterData',
      metadata: {
        context,
      },
      url: this.endpoint.masterDataDelete(masterDataType),
    });
  }

  deleteMasterDataByLanguage(
    masterDataType: number,
    handledErrors: number[] = []
  ): Observable<Response<Record<string, string>>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.delete<Response<Record<string, string>>>({
      operation: 'deleteMasterData',
      metadata: {
        context,
      },
      url: this.endpoint.masterDataDeleteByLanguage(masterDataType),
    });
  }

  fetchClinicalInvestigationList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'clinicalInvestigationList',
      url: this.endpoint.clinicalInvestigationList(),
    });
  }

  fetchLanguagesList(): Observable<Response<Language[]>> {
    return this.get<Response<Language[]>>({
      operation: 'fetchLanguagesList',
      url: this.endpoint.languageList(),
    });
  }

  fetchMasterData(params: Record<string, string | number>): Observable<Response<MasterData[]>> {
    return this.get<Response<MasterData[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchMasterData',
      url: this.endpoint.masterDataList(),
    });
  }

  fetchMasterDataLanguage(idList: number[]): Observable<Response<LanguageMasterData[]>> {
    return this.get<Response<LanguageMasterData[]>>({
      metadata: this.getHttpParams(idList),
      operation: 'fetchMasterData',
      url: this.endpoint.masterDataListByLanguage(),
    });
  }

  fetchMasterDataById(params: Record<string, any>): Observable<Response<masterDataList[]>> {
    return this.get<Response<masterDataList[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchMasterDataById',
      url: this.endpoint.masterDataList(),
    });
  }

  fetchMasterDataByIds(masterDataId: number): Observable<Response<any>> {
    return this.get<Response<masterDataList[]>>({
      operation: 'fetchMasterDataById',
      url: this.endpoint.masterDataListById(masterDataId),
    });
  }

  updateLanguageMasterData(
    masterDataName: LanguageMasterData,
    masterDataId: number
  ): Observable<Response<LanguageMasterData>> {
    return this.put<Response<LanguageMasterData>>({
      body: masterDataName,
      operation: 'updateMasterData',
      url: this.endpoint.masterDataUpdateByLanguage(masterDataId),
    });
  }

  updateMasterData(masterDataName: MasterData, masterDataId: number): Observable<Response<MasterData>> {
    return this.put<Response<MasterData>>({
      body: masterDataName,
      operation: 'updateMasterData',
      url: this.endpoint.masterDataUpdate(masterDataId),
    });
  }

  updateInvestigationData(investigationData: Investigation, investigationId: number): Observable<Response<MasterData>> {
    return this.put<Response<MasterData>>({
      body: investigationData,
      operation: 'updateInvestigationData',
      url: this.endpoint.investigationDataUpdate(investigationId),
    });
  }

  fetchCenterPriceList(params: Record<string, any>): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'centerPriceList',
      url: this.endpoint.centerPriceList(),
    });
  }

  fetchEq5dMasterData(): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      operation: 'eq5dMasterData',
      url: this.endpoint.eq5dMasterData(),
    });
  }

  createManualAuditData(data: ManualAuditItemMaster): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'manualAuditMasterCreate',
      url: this.endpoint.manualAuditMasterCreate(),
    });
  }

  createMedicationData(data: MedicationMaster): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'medicationMasterCreate',
      url: this.endpoint.medicationMasterCreate(),
    });
  }

  createPaymentRatePlan(data: any): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'paymentRatePlanCreate',
      url: this.endpoint.paymentCreate(),
    });
  }

  createSessionCountData(data: any,
    handledErrors: number[] = []): Observable<Response<any>> {
      const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.post<Response<any>>({
      body: data,
      operation: 'createSessionCountData',
      url: this.endpoint.createSessionCount(),
      metadata: {
        context,
      },
    });
  }

  fetchManualAuditItemMasterList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'manualAuditItemMasterList',
      url: this.endpoint.manualAuditMasterList(),
    });
  }

  fetchSessionCountList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchSessionCountList',
      url: this.endpoint.getSessionCount(),
    });
  }

  fetchMedicationMasterList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'medicationMasterList',
      url: this.endpoint.medicationMasterList(),
    });
  }

  fetchMedicationMasterListById(medicationId: number): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      operation: 'medicationMasterListById',
      url: this.endpoint.medicationMasterListById(medicationId),
    });
  }

  fetchRatePlanList(params: Record<string, any> = {}): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'paymentTypeList',
      url: this.endpoint.ratePlanList(),
    });
  }

  updateManualAuditData(
    manualAuditData: ManualAuditItemMaster,
    manualAuditId: number
  ): Observable<Response<MasterData>> {
    return this.put<Response<any>>({
      body: manualAuditData,
      operation: 'manualAuditMasterUpdate',
      url: this.endpoint.manualAuditMasterUpdate(manualAuditId),
    });
  }

  updateMedicationData(medicationData: MedicationMaster, medicationTypeId: number): Observable<Response<MasterData>> {
    return this.put<Response<any>>({
      body: medicationData,
      operation: 'medicationMasterUpdate',
      url: this.endpoint.medicationMasterUpdate(medicationTypeId),
    });
  }

  updatePaymentRatePlan(paymentRateplanData: any, paymentRateplaId: number): Observable<Response<MasterData>> {
    return this.patch<Response<any>>({
      body: paymentRateplanData,
      operation: 'paymentRatePlanUpdate',
      url: this.endpoint.paymentRatePlanUpdate(paymentRateplaId),
    });
  }

  updateSessionCountData(data: any, id: string): Observable<Response<MasterData>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateSessionCountData',
      url: this.endpoint.updateSessionCount(id),
    });
  }

  deleteMedicationData(
    medicationTypeId: number,
    handledErrors: number[] = []
  ): Observable<Response<Record<string, string>>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.delete<Response<Record<string, string>>>({
      operation: 'medicationMasterDelete',

      metadata: {
        context,
      },
      url: this.endpoint.medicationMasterDelete(medicationTypeId),
    });
  }

  deleteManualAuditData(
    manualAuditItemId: number,
    handledErrors: number[] = []
  ): Observable<Response<Record<string, string>>> {
    const context = new HttpContext().set(HANDLED_ERRORS, handledErrors);

    return this.delete<Response<Record<string, string>>>({
      operation: 'manualAuditMasterDelete',

      metadata: {
        context,
      },
      url: this.endpoint.manualAuditMasterDelete(manualAuditItemId),
    });
  }


  fetchGenericConfigList(params: Record<string, string | number>): Observable<Response<MasterData[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchGenericConfigList',
      url: this.endpoint.fetchGenericConfigList(),
    });

  }

  changeGenericConfigStatus(data: any): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'changeGenericConfigStatus',
      url: this.endpoint.changeGenericConfigStatus(),
    });
  }

  addNewConfiguration(data: any): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'addNewConfiguration',
      url: this.endpoint.addNewConfiguration(),
    });
  }


  updateGenConfiguration(data: any, configurationId: number): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateGenConfiguration',
      url: this.endpoint.updateGenConfiguration(configurationId),
    });
  }
    
  createInventoryConfig(data: any): Observable<Response<any>> {
    return this.post<Response<any>>({
      body: data,
      operation: 'createInventoryConfig',
      url: this.endpoint.inventoryConfigCreate(),
    });
  }

  updateInventoryConfig(data: any, id: string): Observable<Response<MasterData>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateInventoryConfig',
      url: this.endpoint.updateInventoryConfig(id),
    });
  }



  fetchInventoryConfigList(params: Record<string, string | number>): Observable<Response<InventoryTimeConfig[]>> {
    return this.get<Response<InventoryTimeConfig[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchInventoryConfigList',
      url: this.endpoint.fetchInventoryConfigList(),
    });

  }

 

  deleteInventoryConfigData(invId: number): Observable<Response<Record<string, string>>> {
    return this.delete<Response<Record<string, string>>>({
      operation: 'deleteInventoryConfigData',
      url: this.endpoint.deleteInventoryConfigData(invId),
    });
  }
  
  fetchSCMConfigList(params: Record<string, string | number>): Observable<Response<MasterData[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchSCMConfigList',
      url: this.endpoint.fetchSCMConfigList(),
    });
  }

  updateSCMConfig(id : number, data: any): Observable<{ data: string; }> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateSCMConfig',
      url: this.endpoint.updateSCMConfig(id),
    });
  }

  fetchCenterWiseToDoList(params: Record<string, string | number>): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchCenterWiseToDoList',
      url: this.endpoint.fetchCenterWiseToDoList()
    })
  }

  fetchTODOMasterList(): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      operation: 'fetchTODOMasterList',
      url: this.endpoint.fetchTODOMasterList()
    })
  }

  saveTODOList(data : any): Observable<Response<any[]>> {
    return this.post<Response<any[]>>({
      body: data,
      operation : 'saveTODOList',
      url: this.endpoint.saveTODOList()
    })
  }


  updateToDoStatus(data: any): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateToDoStatus',
      url: this.endpoint.updateToDoStatus(),
    });
  }

  fetchToDoList(params: Record<string, string | number>): Observable<Response<ToDo[]>> {
    return this.get<Response<ToDo[]>>({
      metadata: { ...this.getHttpParams(params) },
      operation: 'fetchToDoConfigList',
      url: this.endpoint.fetchToDoConfigList(),
    });
  }

  verifyToDoOtp(otp : number): Observable<Response<any>> {
    return this.post<Response<any>>({
      operation: 'verifyToDoOtp',
      url: this.endpoint.verifyToDoOtp(otp),
    })
  }

  sendToDoOtp(): Observable<Response<any>> {
    return this.post<Response<any>>({
      operation: 'sendToDoOtp',
      url: this.endpoint.sendTodoOtp(),
    })
  }

  fetchToDoValidation(): Observable<Response<any>> {
    return this.get<Response<any>>({
      operation: 'fetchcentersTodo',
      url: this.endpoint.fetchToDoValidation()
    })
  }

  getAttrionReasonsByGuestType(guestTypeId: number): Observable<Response<any>> {
    return this.get<Response<any>>({
      operation: 'getAttrionReasonsByGuestType',
      url: this.endpoint.getAttrionReasonsByGuestType(guestTypeId),
    });
  }

  
  createBulkMasterAttrion(masterId: number, guestTypeIds: number[]): Observable<Response<any[]>> {
    return this.post<Response<any[]>>({
      body: {
        masterId,
        guestTypeIds,
      },
      operation: 'createBulkMasterAttrion',
      url: this.endpoint.createBulkMasterAttrion(),
    });
  }

  getMasterAttrionWithDetails(masterId: number): Observable<Response<any[]>> {
    return this.get<Response<any[]>>({
      operation: 'getMasterAttrionWithDetails',
      url: this.endpoint.getMasterAttrionWithDetails(masterId),
    });
  }

  updateMasterAttrionByMaster(masterId: number, data: any): Observable<Response<any>> {
    return this.put<Response<any>>({
      body: data,
      operation: 'updateMasterAttrionByMaster',
      url: this.endpoint.updateMasterAttrionByMaster(masterId),
    });
  }

  deleteMasterAttrion(id: number): Observable<Response<any>> {
    return this.delete<Response<any>>({
      operation: 'deleteMasterAttrion',
      url: this.endpoint.deleteMasterAttrion(id),
    });
  }


}
